import * as React from 'react';
import {
  root as rootCls,
  filename as filenameCls,
  meta as metaCls,
  actions as actionsCls,
  view as viewCls,
  remove as removeCls,
} from './FileCard.css';

export interface FileCardProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'children' | 'onClick'
> {
  /** The artifact filename — the card's title. */
  filename: string;
  /** The muted meta line, composed by the caller (e.g. `PDF · 40 KB · 19 Jul`). */
  meta: string;
  /** Open the file for viewing. */
  onView: () => void;
  /** Hard-delete the file. */
  onRemove: () => void;
  /** Disable Remove (e.g. while a delete is in flight). Default `false`. */
  removeDisabled?: boolean;
  /** React 19 ref-as-prop. */
  ref?: React.Ref<HTMLDivElement>;
}

/**
 * FileCard — one agent-produced artifact in the Drop-6 Files rail: a bold
 * filename, a muted `type · size · date` meta line, and View / Remove actions
 * rendered as focusable link-styled buttons. Real fields only (no Replace).
 *
 * The **filename is itself a View target** — clicking a file's title is what
 * people try first, and a title that does nothing reads as a broken row. It is
 * a real `<button>` (not a click handler on the card): the row-wide target stays
 * keyboard-reachable, and nesting the Remove button inside a clickable card
 * would be invalid. The explicit `View` action stays for discoverability.
 *
 * The `meta` string is composed by the caller so the card stays presentational.
 * Each *action's* accessible name carries the filename (`View {filename}` /
 * `Remove {filename}`), so a screen-reader user isn't left with a list of
 * identical "View" buttons; the title button is named by the filename itself.
 */
export function FileCard({
  filename,
  meta,
  onView,
  onRemove,
  removeDisabled = false,
  className,
  ref,
  ...rest
}: FileCardProps) {
  const cls = [rootCls, className].filter(Boolean).join(' ');
  return (
    <div ref={ref} className={cls} {...rest}>
      <button type="button" className={filenameCls} onClick={onView}>
        {filename}
      </button>
      <div className={metaCls}>{meta}</div>
      <div className={actionsCls}>
        <button type="button" className={viewCls} onClick={onView} aria-label={`View ${filename}`}>
          View
        </button>
        <button
          type="button"
          className={removeCls}
          onClick={onRemove}
          disabled={removeDisabled}
          aria-label={`Remove ${filename}`}
        >
          Remove
        </button>
      </div>
    </div>
  );
}
FileCard.displayName = 'FileCard';
